import { useCallback, useEffect, useMemo, useState } from "react";
import TableButton from "components/atoms/buttons/TableButton";
import FirmwareDWPresenter from "./FirmwareDWPresenter";
import * as s from "./FirmwareDWStyled";
import { useQuery, useQueryClient } from "react-query";
import { getFirmwareDownloadUrl, getFirmwareList } from "api/settingAPI";
import {
  BasePagination,
  CustomError,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { Location, useLocation } from "react-router-dom";
import { isNull } from "lodash";
import { TableColumnsType } from "antd";
import { ColumnType } from "antd/es/table";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { useMenu } from "hook/useMenu";
import useApiError from "hook/useApiError";
import Type from "components/atoms/text/labels/Type";
import { RecorderType } from "utils/RecorderUtil";
import DeactivateButton from "components/blocks/modal/deactivate";
import { DeactiveButtonFunc } from "components/blocks/modal/deactivate/DeactivateButton";
import { formatBytesAndUnits } from "utils/functions";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { FirmwarePackageListResult } from "api/interfaces/settingsInterface.interface";
import { deleteFirmwarePackage } from "api/settingAPI";
import useStatusMutation from "hook/useStatusMutation";
import { firmwareErrorKey } from "./add/AddModalContainer";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { covaModelOptions } from "utils/options";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import { useSelector } from "react-redux";
import { selectStomp } from "redux/stomp/stompSelector";
import { StatusType } from "components/blocks/status/Status";

interface FirmwareDWQueryInfo extends BasePagination {
  model?: string;
}

const initialQueryInfo = {
  ...baseQueryInfo,
  sortType: "createdAt",
  sortDirection: SORT_DIRECTION.DESC,
};

export default function FirmwareDWContainer(): JSX.Element {
  const location: Location = useLocation();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const queryClient = useQueryClient();
  const [isAddModal, setIsAddModal] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);

  const [selectedRow, setSelectedRow] = useState<
    FirmwarePackageListResult | undefined
  >();

  const [paginationInfo, setPaginationInfo] = useState<FirmwareDWQueryInfo>({
    ...initialQueryInfo,
  });
  const queryKey = useQueryKey(paginationInfo);
  const { firmware } = useSelector(selectStomp);

  const { queryNavigate } = useMenu({});
  const { lang, notifySuccess, handleErrorNotify } = useApiError();

  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      setPaginationInfo((info) => {
        return {
          ...info,
          model: isNull(changeParam.get("model"))
            ? 0
            : (changeParam.get("model") as string),
          pageNum: isNull(changeParam.get("pageNum"))
            ? 0
            : Number(changeParam.get("pageNum")),
          pageLimit: isNull(changeParam.get("pageLimit"))
            ? 20
            : Number(changeParam.get("pageLimit")),
          keyword: isNull(changeParam.get("keyword"))
            ? ""
            : (changeParam.get("keyword") as string),
          sortType: isNull(changeParam.get("sortType"))
            ? "createdAt"
            : (changeParam.get("sortType") as string),
          sortDirection: isNull(changeParam.get("sortDirection"))
            ? SORT_DIRECTION.DESC
            : (changeParam.get("sortDirection") as SORT_DIRECTION),
        } as FirmwareDWQueryInfo;
      });
    } else {
      setPaginationInfo((info) => {
        return {
          ...info,
          pageNum: 0,
          pageLimit: 20,
          keyword: "",
          sortType: "createdAt",
          sortDirection: SORT_DIRECTION.DESC,
        };
      });
    }
  }, [location]);

  const [status, setStatus] = useState<StatusType>({
    totalTitle: "Total",
    totalValue: 0,
    items: [],
    isMobileShow: {
      totalTitle: true,
      totalValue: true,
      items: true,
    },
    cloudStatus: false,
  });
  const getDwFirmwareList = useQuery(
    ["getDwFirmwareList", queryKey, firmware],
    () =>
      getFirmwareList({
        payload: paginationInfo,
      }),

    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        // console.log(res, "res Notice!!");
        if (res.error !== 0) {
          setPaginationInfo({ ...initialQueryInfo });
          return;
        }
        setPaginationInfo((info) => {
          return { ...info, ...res.page };
        });
        setStatus((prev) => {
          return {
            ...prev,
            totalValue: res.page.total,
          };
        });
      },
      onError: (e: any) => {
        setPaginationInfo({ ...initialQueryInfo });
      },
    }
  );

  // 추후 API 추가후 useQuery의 isLoading 사용

  const data = useMemo(() => {
    //추후 API 추가후 useQuery의 data 사용
    return getDwFirmwareList.data?.result || [];
  }, [getDwFirmwareList.data]);

  const onHeaderCell = (column: ColumnType<FirmwarePackageListResult>) => {
    return {
      onClick: () => {
        if (column.key) {
          queryNavigate({
            to: location.pathname,
            queryInfo: {
              ...paginationInfo,
              pageNum: 0,
              sortType: column.key as string,
              sortDirection: onSortAntd(paginationInfo.sortDirection),
            },
          });
        }
      },
    };
  };

  const mutationDeleteDwFirmware = useStatusMutation(deleteFirmwarePackage, {
    onSuccess: (res) => {
      if (res.error !== 0) {
        handleErrorNotify(
          res.error,
          lang.label.setting.firmware.notify.delete.fail,
          firmwareErrorKey
        );
        return;
      }
      notifySuccess(lang.label.setting.firmware.notify.delete.success);
      queryClient.invalidateQueries("getDwFirmwareList");
      setSelectedRow(undefined);
      setIsAddModal(false);
      setIsEditModal(false);
    },
    onError: (err: CustomError) => {
      handleErrorNotify(
        err,
        lang.label.setting.firmware.notify.delete.fail,
        firmwareErrorKey
      );
    },
  });
  const onDelete = useCallback(
    (id: string) => {
      mutationDeleteDwFirmware.mutate(id);
    },
    [mutationDeleteDwFirmware]
  );

  const mutationGetDownloadUrl = useStatusMutation(getFirmwareDownloadUrl, {
    onSuccess: (res) => {
      if (res.error !== 0) {
        handleErrorNotify(
          res.error,
          lang.label.setting.firmware.notify.download.fail,
          firmwareErrorKey
        );
        return;
      }
      window.open(res.result.downloadUrl, "_blank");
    },
    onError: (err) => {
      handleErrorNotify(
        err,
        lang.label.setting.firmware.notify.download.fail,
        firmwareErrorKey
      );
    },
  });

  const onDownloadFile = (id: string) => {
    mutationGetDownloadUrl.mutate(id);
  };

  const onChangeModel = (value: string | undefined) => {
    if (value !== undefined) {
      queryNavigate({
        to: location.pathname,
        queryInfo: {
          ...paginationInfo,
          pageNum: 0,
          model: value,
        } as FirmwareDWQueryInfo,
      });
    }
  };
  const columns: TableColumnsType<FirmwarePackageListResult> = [
    {
      title: "Recorder Type",
      key: "recorderType",
      dataIndex: "recorderType",
      render: (value, row) => <Type type={value as RecorderType} />,
      width: 130,
    },
    {
      title: (
        <TableColumnFilterTitle
          title="Model"
          filters={covaModelOptions}
          onClick={onChangeModel}
          data-testid="maintenance-firmware-firmware-dw-table-column-filter-title-change-model"
        />
      ),

      key: "model",
      dataIndex: "model",
      render: (value, row) => value,
      width: "15%",
    },
    {
      title: "Version",
      key: "version",
      dataIndex: "version",
      render: (value, row) => {
        return (
          <s.Row>
            {value} {row.isActive && <s.ActiveLabel>ACTIVE</s.ActiveLabel>}
          </s.Row>
        );
      },
      sorter: true,
      sortOrder: changeSortOrderAntd(paginationInfo, "version"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Date",
      key: "createdAt",
      dataIndex: "createdAt",
      render: (value, row) => {
        return getAccountTimeFormatDate(value, selectedAccount, true, true);
      },
      sorter: true,
      sortOrder: changeSortOrderAntd(paginationInfo, "createdAt"),
      onHeaderCell: onHeaderCell,
      width: "15%",
      responsive: ["xl"],
    },
    {
      title: "Size",
      key: "fileSize",
      dataIndex: "fileSize",
      render: (value, row) => {
        const { value: formattedValue, unit } = formatBytesAndUnits(value);
        return `${formattedValue} ${unit}`;
      },
      sorter: true,
      sortOrder: changeSortOrderAntd(paginationInfo, "fileSize"),
      onHeaderCell: onHeaderCell,
      width: "10%",
      responsive: ["lg"],
    },
    // 마지막 버튼
    {
      title: "",
      render: (value, row, index) => (
        <s.ButtonWrapper>
          <TableButton
            label="Release Notes"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRow(row);
              onModal("viewReleaseNotes");
            }}
            data-testid={`maintenance-firmware-release-notes-${index}`}
          />

          <TableButton
            label="Download"
            onClick={(e) => {
              e.stopPropagation();
              onDownloadFile(row.id as string);
            }}
            data-testid={`maintenance-firmware-firmware-dw-table-button-download-${index}`}
          />

          <TableButton
            label="Edit"
            onClick={(e) => {
              e.stopPropagation();
              onModal("edit");
              setSelectedRow(row);
            }}
            data-testid={`maintenance-firmware-firmware-dw-table-button-edit-${index}`}
          />

          <DeactivateButton
            title="Do you really want to delete this firmware package?"
            subTitle="It cannot be recovered."
            onDeactive={() => onDelete(row.id)}
            button={(props: DeactiveButtonFunc) => (
              <TableButton
                {...props}
                label="Delete"
                buttonType="warning"
                data-testid={`maintenance-firmware-table-button-delete-${index}`}
              />
            )}
            data-testid={`maintenance-firmware-deactivate-button-${index}`}
          />
        </s.ButtonWrapper>
      ),

      align: "right",
      width: 410,
    },
  ];

  const [isViewReleaseNotesModal, setIsViewReleaseNotesModal] = useState(false);
  const onModal = (type?: string) => {
    if (type === "viewReleaseNotes") {
      setIsViewReleaseNotesModal(!isViewReleaseNotesModal);
    }
    if (type === "add") {
      setIsAddModal(!isAddModal);
    }
    if (type === "edit") {
      setIsEditModal(!isEditModal);
    }
  };

  const onChangePage = (page: number, totalRows: number) => {
    if (page !== paginationInfo.pageNum + 1) {
      queryNavigate({
        to: location.pathname,
        queryInfo: {
          pageLimit: paginationInfo.pageLimit,
          pageNum: page - 1,
          keyword: paginationInfo.keyword as string,
          sortType: paginationInfo.sortType,
          sortDirection: paginationInfo.sortDirection as SORT_DIRECTION,
        },
      });
    }
  };

  const onClickSearch = (keyword: string) => {
    queryNavigate({
      to: location.pathname,
      queryInfo: {
        ...paginationInfo,
        pageNum: 0,
        keyword: keyword,
      },
    });
  };

  return (
    <FirmwareDWPresenter
      isViewReleaseNotesModal={isViewReleaseNotesModal}
      onModal={onModal}
      isAddModal={isAddModal}
      isEditModal={isEditModal}
      columns={columns}
      data={data}
      selectedRow={selectedRow}
      onChangePage={onChangePage}
      paginationInfo={paginationInfo}
      tablePending={getDwFirmwareList.isLoading}
      isLoading={
        mutationDeleteDwFirmware.isLoading || mutationGetDownloadUrl.isLoading
      }
      onDownloadFile={onDownloadFile}
      onDelete={onDelete}
      status={status}
      onClickSearch={onClickSearch}
    />
  );
}
